import React, { useState, useEffect, useMemo } from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import {
  getStudioBookings,
  updateStudioBooking,
  MarketplaceBooking,
  BookingStatus,
} from "../../../../Redux/ApiCalls/Dashboard/MarketplaceAPI";
import { showSuccessToast, showErrorToast } from "../../../atoms/Utlis/Toast";
import { device } from "../../../atoms/Utlis/media";
import {
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
  MessageSquare,
  Phone,
  Sparkles,
  SearchX,
  CalendarCheck,
  History,
  Loader2,
  IndianRupee,
  StickyNote,
  ArrowRight,
} from "lucide-react";
import MarketplaceEmptyState, { MarketplaceLoadingSpinner } from "./MarketplaceEmptyState";
import {
  STATUS_COLOR,
  STATUS_LABELS,
  VALID_TRANSITIONS,
  FILTER_OPTIONS,
} from "./marketplaceConstants";
import {
  PageContainer,
  Hero,
  HeroGlow,
  HeroContent,
  HeroText,
  HeroBadge,
  PageTitle,
  PageSubtitle,
  Panel,
  Toolbar,
  SecondaryButton,
} from "../quotationMasterListShared";
import { ToolbarRight } from "../documentSharedStyles";
import { ListHeader, ListTitle, CountBadge } from "../../../atoms/Dashboard/CustomerTransaction/transactionTableStyles";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Spinner = styled(Loader2)`
  animation: ${spin} 0.8s linear infinite;
`;

const gradient =
  "linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%)";

const BookingsPage = styled(PageContainer)`
  height: auto;
  min-height: 100%;
  overflow: visible;
  flex-shrink: 0;
`;

const BookingsPanel = styled(Panel)`
  flex: none;
  min-height: unset;
  overflow: visible;
`;

const MarketplaceBookingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<MarketplaceBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<BookingStatus | "">("");
  const [notesMap, setNotesMap] = useState<Record<string, string>>({});
  const [updating, setUpdating] = useState<string | null>(null);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const res = await getStudioBookings(statusFilter ? { status: statusFilter } : undefined);
      const data = res?.data;
      setBookings(Array.isArray(data) ? data : []);
    } catch {
      showErrorToast("Failed to load bookings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, [statusFilter]);

  const handleStatusUpdate = async (booking: MarketplaceBooking, newStatus: BookingStatus) => {
    setUpdating(booking.id);
    try {
      await updateStudioBooking(booking.id, {
        status: newStatus,
        studio_notes: notesMap[booking.id] || "",
      });
      showSuccessToast(`Booking marked as ${newStatus}`);
      fetchBookings();
    } catch (err: any) {
      showErrorToast(err?.response?.data?.message || "Failed to update booking");
    } finally {
      setUpdating(null);
    }
  };

  const safeBookings = Array.isArray(bookings) ? bookings : [];

  const grouped = useMemo(
    () => ({
      pending: safeBookings.filter((b) => ["requested", "pending_payment"].includes(b.status)),
      active: safeBookings.filter((b) => ["confirmed", "accepted"].includes(b.status)),
      done: safeBookings.filter((b) => ["completed", "rejected", "cancelled"].includes(b.status)),
    }),
    [safeBookings]
  );

  const stats = useMemo(
    () => ({
      total: safeBookings.length,
      pending: grouped.pending.length,
      active: grouped.active.length,
      done: grouped.done.length,
    }),
    [safeBookings, grouped]
  );

  const renderBookingCard = (booking: MarketplaceBooking) => {
    const transitions = VALID_TRANSITIONS[booking.status] || [];
    const isUpdating = updating === booking.id;
    const customerName = booking.customer_name || booking.customer_username || "Unknown";
    const initials = customerName
      .split(" ")
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() || "")
      .join("");

    return (
      <BookingCard key={booking.id}>
        <CardHeader>
          <CardLeft>
            <RefBadge>{booking.reference}</RefBadge>
            <ListingTitle>{booking.listing_title}</ListingTitle>
          </CardLeft>
          <CardRight>
            <StatusChip $color={STATUS_COLOR[booking.status] || "#6b7280"}>
              {STATUS_LABELS[booking.status] || booking.status}
            </StatusChip>
          </CardRight>
        </CardHeader>

        <CardBody>
          <CustomerStrip>
            <Avatar>{initials || "?"}</Avatar>
            <CustomerMeta>
              <CustomerName>{customerName}</CustomerName>
              <CustomerSub>Inquiry for this listing</CustomerSub>
            </CustomerMeta>
          </CustomerStrip>

          <DetailGrid>
            <DetailTile $tone="#7c4dff">
              <TileIcon $tone="#7c4dff">
                <Phone size={15} />
              </TileIcon>
              <TileContent>
                <TileLabel>Contact</TileLabel>
                <TileValue>
                  {booking.customer_phone ? (
                    <WhatsAppChip
                      href={`https://wa.me/${(booking.customer_country_code || "+91").replace("+", "")}${booking.customer_phone}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {booking.customer_country_code || "+91"} {booking.customer_phone}
                      <ArrowRight size={12} />
                    </WhatsAppChip>
                  ) : (
                    "N/A"
                  )}
                </TileValue>
              </TileContent>
            </DetailTile>

            <DetailTile $tone="#35b7c8">
              <TileIcon $tone="#35b7c8">
                <Calendar size={15} />
              </TileIcon>
              <TileContent>
                <TileLabel>Date Range</TileLabel>
                <TileValue>
                  {booking.date_from
                    ? `${booking.date_from} → ${booking.date_to || "—"}`
                    : "No dates set"}
                </TileValue>
              </TileContent>
            </DetailTile>

            <DetailTile $tone="#c94daa" $span>
              <TileIcon $tone="#c94daa">
                <IndianRupee size={15} />
              </TileIcon>
              <TileContent>
                <TileLabel>Total Amount</TileLabel>
                <PriceValue>₹{Number(booking.amount_due).toLocaleString("en-IN")}</PriceValue>
              </TileContent>
            </DetailTile>
          </DetailGrid>

          {booking.customer_notes && (
            <QuoteBox>
              <QuoteIcon>
                <MessageSquare size={16} />
              </QuoteIcon>
              <QuoteContent>
                <QuoteLabel>Customer message</QuoteLabel>
                <QuoteText>{booking.customer_notes}</QuoteText>
              </QuoteContent>
            </QuoteBox>
          )}

          <NotesCard>
            <NotesCardHeader>
              <NotesCardIcon>
                <StickyNote size={14} />
              </NotesCardIcon>
              <span>Studio Notes</span>
              <OptionalTag>optional</OptionalTag>
            </NotesCardHeader>
            <StudioNotesInput
              value={notesMap[booking.id] || booking.studio_notes || ""}
              onChange={(e) => setNotesMap((prev) => ({ ...prev, [booking.id]: e.target.value }))}
              placeholder="Add a note visible to the customer when you update this booking..."
              rows={2}
            />
          </NotesCard>

          {transitions.length > 0 && (
            <ActionFooter>
              <ActionFooterLabel>Quick actions</ActionFooterLabel>
              <ActionsRow>
                {transitions.includes("accepted") && (
                  <ActionBtn
                    $variant="success"
                    disabled={isUpdating}
                    onClick={() => handleStatusUpdate(booking, "accepted")}
                  >
                    {isUpdating ? <Spinner size={15} /> : <CheckCircle size={15} />}
                    Accept
                  </ActionBtn>
                )}
                {transitions.includes("rejected") && (
                  <ActionBtn
                    $variant="danger"
                    disabled={isUpdating}
                    onClick={() => handleStatusUpdate(booking, "rejected")}
                  >
                    {isUpdating ? <Spinner size={15} /> : <XCircle size={15} />}
                    Reject
                  </ActionBtn>
                )}
                {transitions.includes("completed") && (
                  <ActionBtn
                    $variant="primary"
                    disabled={isUpdating}
                    onClick={() => handleStatusUpdate(booking, "completed")}
                  >
                    {isUpdating ? <Spinner size={15} /> : <CheckCircle size={15} />}
                    Mark Completed
                  </ActionBtn>
                )}
                {transitions.includes("cancelled") && (
                  <ActionBtn
                    $variant="muted"
                    disabled={isUpdating}
                    onClick={() => handleStatusUpdate(booking, "cancelled")}
                  >
                    Cancel
                  </ActionBtn>
                )}
              </ActionsRow>
            </ActionFooter>
          )}
        </CardBody>
      </BookingCard>
    );
  };

  const renderSection = (
    label: string,
    icon: React.ReactNode,
    color: string,
    items: MarketplaceBooking[]
  ) => {
    if (items.length === 0) return null;
    return (
      <Section>
        <SectionLabel $color={color}>
          {icon} {label} ({items.length})
        </SectionLabel>
        <CardsGrid>{items.map(renderBookingCard)}</CardsGrid>
      </Section>
    );
  };

  return (
    <BookingsPage>
      <Hero>
        <HeroGlow />
        <HeroContent>
          <HeroText>
            <HeroBadge>
              <Sparkles size={11} />
              Marketplace
            </HeroBadge>
            <PageTitle className="linear-text">Bookings</PageTitle>
            <PageSubtitle>
              Manage incoming booking requests and inquiries for your listings.
            </PageSubtitle>
          </HeroText>
          <SecondaryButton
            type="button"
            onClick={() => navigate("/dashboard/marketplace/listings")}
            style={{
              width: "auto",
              minWidth: 168,
              height: 46,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
          >
            Manage Listings
          </SecondaryButton>
        </HeroContent>
      </Hero>

      <StatsGrid>
        <WalletHero>
          <WalletIconWrap>
            <Calendar size={16} />
          </WalletIconWrap>
          <WalletHeroBody>
            <WalletHeroValue>{stats.total}</WalletHeroValue>
            <WalletHeroLabel>Total bookings</WalletHeroLabel>
          </WalletHeroBody>
        </WalletHero>

        <StatCard $tone="#f09a3e">
          <StatIcon $tone="#f09a3e">
            <Clock size={16} />
          </StatIcon>
          <StatBody>
            <StatValue>{stats.pending}</StatValue>
            <StatLabel>Pending</StatLabel>
          </StatBody>
        </StatCard>

        <StatCard $tone="#31a66a">
          <StatIcon $tone="#31a66a">
            <CalendarCheck size={16} />
          </StatIcon>
          <StatBody>
            <StatValue>{stats.active}</StatValue>
            <StatLabel>Active</StatLabel>
          </StatBody>
        </StatCard>

        <StatCard $tone="#6b7280">
          <StatIcon $tone="#6b7280">
            <History size={16} />
          </StatIcon>
          <StatBody>
            <StatValue>{stats.done}</StatValue>
            <StatLabel>Past</StatLabel>
          </StatBody>
        </StatCard>
      </StatsGrid>

      <BookingsPanel>
        <Toolbar>
          <ListHeader style={{ padding: 0, margin: 0 }}>
            <ListTitle>Booking records</ListTitle>
            {!loading && safeBookings.length > 0 && (
              <CountBadge>{safeBookings.length} shown</CountBadge>
            )}
          </ListHeader>
          <ToolbarRight>
            <FilterSelect
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as BookingStatus | "")}
            >
              {FILTER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </FilterSelect>
          </ToolbarRight>
        </Toolbar>

        <PanelContent>
          {loading ? (
            <MarketplaceLoadingSpinner padding="60px 22px" />
          ) : safeBookings.length === 0 ? (
            <MarketplaceEmptyState
              padding="32px 22px 48px"
              icon={
                statusFilter ? (
                  <SearchX size={36} strokeWidth={1.5} />
                ) : (
                  <Calendar size={36} strokeWidth={1.5} />
                )
              }
              title={statusFilter ? "No bookings found" : "No bookings yet"}
              description={
                statusFilter
                  ? `No bookings match the "${FILTER_OPTIONS.find((o) => o.value === statusFilter)?.label || statusFilter}" filter. Try a different status or view all bookings.`
                  : "When customers book your listings, requests will appear here. Keep your listings active to start receiving bookings."
              }
              actions={[
                ...(statusFilter
                  ? [
                      {
                        label: "Clear filter",
                        onClick: () => setStatusFilter(""),
                        variant: "secondary" as const,
                      },
                    ]
                  : []),
                {
                  label: "Manage Listings",
                  onClick: () => navigate("/dashboard/marketplace/listings"),
                  variant: statusFilter ? ("secondary" as const) : undefined,
                },
              ]}
              hint={
                !statusFilter
                  ? {
                      icon: <Sparkles size={13} />,
                      text: "Accept, reject, or mark bookings complete from one place",
                    }
                  : undefined
              }
            />
          ) : (
            <>
              {renderSection(
                "Pending Action",
                <Clock size={14} />,
                "#f09a3e",
                grouped.pending
              )}
              {renderSection(
                "Active Bookings",
                <CheckCircle size={14} />,
                "#16a34a",
                grouped.active
              )}
              {renderSection("Past", <CheckCircle size={14} />, "#6b7280", grouped.done)}
            </>
          )}
        </PanelContent>
      </BookingsPanel>
    </BookingsPage>
  );
};

export default MarketplaceBookingsPage;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const WalletHero = styled.div`
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  color: #fff;
  background: ${gradient};
  box-shadow: 0 8px 22px rgba(201, 77, 170, 0.22);
  text-align: left;

  &::after {
    content: "";
    position: absolute;
    right: -20px;
    bottom: -24px;
    width: 90px;
    height: 90px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.12);
  }
`;

const WalletIconWrap = styled.div`
  position: relative;
  z-index: 1;
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.22);
`;

const WalletHeroBody = styled.div`
  position: relative;
  z-index: 1;
  min-width: 0;
`;

const WalletHeroValue = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #fff;
  line-height: 1.1;
`;

const WalletHeroLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.88);
  margin-top: 2px;
`;

const StatCard = styled.div<{ $tone: string }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 6px 18px rgba(23, 23, 23, 0.04);
`;

const StatIcon = styled.div<{ $tone: string }>`
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${(p) => p.$tone};
  background: ${(p) => `${p.$tone}18`};
`;

const StatBody = styled.div`
  min-width: 0;
`;

const StatValue = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #171717;
  line-height: 1.1;
`;

const StatLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.5);
  margin-top: 2px;
`;

const FilterSelect = styled.select`
  height: 46px;
  padding: 0 14px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  border-radius: 12px;
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 600;
  background: #fafafa;
  color: #171717;
  cursor: pointer;
  outline: none;
  min-width: 180px;

  &:focus {
    border-color: rgba(201, 77, 170, 0.35);
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.08);
    background: #fff;
  }
`;

const PanelContent = styled.div`
  flex: none;
  overflow: visible;
  padding: 0 22px 24px;
`;

const Section = styled.div`
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media ${device.tablet} {
    grid-template-columns: 1fr;
  }
`;

const SectionLabel = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  color: ${(p) => p.$color};
  font-family: Montserrat, sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const BookingCard = styled.div`
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.06);
  border-radius: 18px;
  overflow: hidden;
  animation: ${fadeUp} 0.3s ease;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(23, 23, 23, 0.08);
    background: #fff;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  gap: 12px;
  background: linear-gradient(180deg, rgba(201, 77, 170, 0.04) 0%, transparent 100%);
  border-bottom: 1px solid rgba(23, 23, 23, 0.05);
`;

const CardLeft = styled.div`
  flex: 1;
  min-width: 0;
`;

const RefBadge = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  font-weight: 800;
  color: #c94daa;
  display: inline-block;
  padding: 3px 9px;
  background: rgba(201, 77, 170, 0.08);
  border-radius: 6px;
  margin-bottom: 6px;
`;

const ListingTitle = styled.div`
  font-size: 16px;
  font-weight: 800;
  color: #171717;
  letter-spacing: -0.01em;
`;

const CardRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const StatusChip = styled.span<{ $color: string }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 100px;
  font-size: 11px;
  font-weight: 700;
  font-family: Montserrat, sans-serif;
  background: ${(p) => p.$color}18;
  color: ${(p) => p.$color};
  white-space: nowrap;
`;

const CardBody = styled.div`
  padding: 18px 20px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const CustomerStrip = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 14px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.08) 0%,
    rgba(206, 86, 176, 0.06) 100%
  );
  border: 1px solid rgba(201, 77, 170, 0.1);
`;

const Avatar = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 12px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 800;
  color: #fff;
  background: ${gradient};
  box-shadow: 0 4px 14px rgba(201, 77, 170, 0.25);
`;

const CustomerMeta = styled.div`
  min-width: 0;
`;

const CustomerName = styled.div`
  font-size: 15px;
  font-weight: 800;
  color: #171717;
  letter-spacing: -0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CustomerSub = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.48);
  margin-top: 2px;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const DetailTile = styled.div<{ $tone: string; $span?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 13px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
  grid-column: ${(p) => (p.$span ? "1 / -1" : "auto")};
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: ${(p) => `${p.$tone}30`};
    box-shadow: 0 4px 16px ${(p) => `${p.$tone}12`};
  }
`;

const TileIcon = styled.div<{ $tone: string }>`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(p) => p.$tone};
  background: ${(p) => `${p.$tone}14`};
`;

const TileContent = styled.div`
  min-width: 0;
  flex: 1;
`;

const TileLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(23, 23, 23, 0.42);
  margin-bottom: 3px;
`;

const TileValue = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #171717;
  line-height: 1.35;
  word-break: break-word;
`;

const PriceValue = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 20px;
  font-weight: 800;
  letter-spacing: -0.02em;
  background: linear-gradient(135deg, #ffc951 0%, #c94daa 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const WhatsAppChip = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 100px;
  font-size: 12px;
  font-weight: 700;
  color: #16a34a;
  background: rgba(22, 163, 74, 0.08);
  border: 1px solid rgba(22, 163, 74, 0.18);
  text-decoration: none;
  transition: all 0.15s ease;
  max-width: 100%;

  svg {
    flex-shrink: 0;
    opacity: 0.7;
  }

  &:hover {
    background: rgba(22, 163, 74, 0.14);
    border-color: rgba(22, 163, 74, 0.3);
    transform: translateX(2px);
  }
`;

const QuoteBox = styled.div`
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 14px;
  background: rgba(124, 77, 255, 0.04);
  border: 1px solid rgba(124, 77, 255, 0.12);
  border-left: 3px solid #7c4dff;
`;

const QuoteIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 9px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #7c4dff;
  background: rgba(124, 77, 255, 0.1);
`;

const QuoteContent = styled.div`
  min-width: 0;
  flex: 1;
`;

const QuoteLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(124, 77, 255, 0.75);
  margin-bottom: 4px;
`;

const QuoteText = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.78);
  line-height: 1.55;
  font-family: Montserrat, sans-serif;
`;

const NotesCard = styled.div`
  padding: 14px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid rgba(23, 23, 23, 0.07);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const NotesCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(23, 23, 23, 0.5);
`;

const NotesCardIcon = styled.span`
  width: 26px;
  height: 26px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #f09a3e;
  background: rgba(240, 154, 62, 0.12);
`;

const OptionalTag = styled.span`
  margin-left: auto;
  padding: 2px 8px;
  border-radius: 100px;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(23, 23, 23, 0.4);
  background: rgba(23, 23, 23, 0.05);
`;

const StudioNotesInput = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  padding: 12px 14px;
  border: 1px solid rgba(23, 23, 23, 0.08);
  border-radius: 12px;
  font-size: 13px;
  font-family: Urbanist, sans-serif;
  font-weight: 600;
  background: #fafafa;
  resize: vertical;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;

  &:focus {
    outline: none;
    border-color: rgba(201, 77, 170, 0.4);
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.1);
    background: #fff;
  }

  &::placeholder {
    color: rgba(23, 23, 23, 0.35);
    font-weight: 500;
  }
`;

const ActionFooter = styled.div`
  padding-top: 14px;
  margin-top: 2px;
  border-top: 1px dashed rgba(23, 23, 23, 0.1);
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ActionFooterLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(23, 23, 23, 0.38);
`;

const ActionsRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const actionVariants = {
  success: { bg: "#16a34a", shadow: "rgba(22, 163, 74, 0.3)" },
  danger: { bg: "#ef4444", shadow: "rgba(239, 68, 68, 0.3)" },
  primary: { bg: "#7c4dff", shadow: "rgba(124, 77, 255, 0.3)" },
  muted: { bg: "#6b7280", shadow: "rgba(107, 114, 128, 0.3)" },
};

const ActionBtn = styled.button<{ $variant: keyof typeof actionVariants }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 40px;
  padding: 0 18px;
  border: none;
  border-radius: 10px;
  background: ${(p) => actionVariants[p.$variant].bg};
  color: white;
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 12px ${(p) => actionVariants[p.$variant].shadow};
  transition: all 0.2s;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px ${(p) => actionVariants[p.$variant].shadow};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;
